import { useState, useEffect } from 'react';
import './styles/global.css';
import './styles/tactile-archive.css';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { supabase, db } from './lib/supabase';
import { useData } from './hooks/useData';
import AuthPage from './pages/AuthPage';
import WaitlistPopup from './components/WaitlistPopup';
import AspreoShell from './components/AspreoShell';
import ProfilePage from './pages/ProfilePage';
import { Toast, C } from './components/UI';

// Lazy-load the diary features (original App logic adapted)
import DiaryApp, { ThemeProvider } from './DiaryApp';
import { MFALoginChallenge } from './components/MFAGate';
import AspreoSite from './pages/AspreoSite';
import PublicBookingPage from './pages/PublicBookingPage';
import PublicInvoicePage   from './pages/PublicInvoicePage';
import PublicCheckoutPage from './pages/PublicCheckoutPage';
import SuperAdminPage       from './pages/SuperAdminPage';
import WorkspaceInvitePage  from './pages/WorkspaceInvitePage';
import PublicProposalPage from './pages/PublicProposalPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';

function LoadingScreen() {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', background:'#0f172a', flexDirection:'column', gap:16, fontFamily:"'Inter',sans-serif" }}>
      <div style={{ width:64, height:64, borderRadius:18, background:'linear-gradient(135deg,#6366f1,#8b5cf6)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:30, boxShadow:'0 8px 32px rgba(99,102,241,.4)', animation:'pulse-glow 2s infinite' }}>📖</div>
      <div style={{ color:'#94a3b8', fontSize:15 }}>Loading Aspreo Life...</div>
      <div style={{ width:200, height:3, background:'#1e293b', borderRadius:99, overflow:'hidden' }}>
        <div style={{ height:'100%', background:'linear-gradient(90deg,#6366f1,#8b5cf6)', borderRadius:99, animation:'shimmer 1.5s infinite', backgroundSize:'200% 100%' }}/>
      </div>
    </div>
  );
}

function MainApp() {
  const { user, loading: authLoading, profile, newLoginAlert, dismissNewLoginAlert } = useAuth();
  const data = useData();
  const [view,            setView]           = useState('dashboard');

  // ── Public booking page detection ─────────────────────────────────────────
  const bookingMatch = window.location.pathname.match(/^\/book\/([^/]+)/);
  const bookingUsername = bookingMatch ? bookingMatch[1] : null;

  // Handle invite URL param — auto-accept invite when user logs in via invite link
  const [inviteProjectId, setInviteProjectId] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pid    = params.get('invited_to');
    const reject = params.get('reject_invite');
    const email  = params.get('email');
    if (pid) setInviteProjectId(pid);
    // Handle rejection via URL (no login needed — just mark declined)
    if (reject && email) {
      (async () => {
        try {
          await db.acceptInviteByEmail(decodeURIComponent(email), null, 'rejected');
          window.history.replaceState({}, '', window.location.pathname);
        } catch {}
      })();
    }
    // ── Clear gcal_error / gcal_connected params immediately on load ──────────
    // These are set by /api/auth/google-callback after OAuth redirect
    const gcalError     = params.get('gcal_error');
    const gcalConnected = params.get('gcal_connected');
    if (gcalError || gcalConnected) {
      // Strip the param from the URL without a page reload
      window.history.replaceState({}, '', window.location.pathname);
      if (gcalError) {
        // Show the error as a toast so the user knows what happened
        setTimeout(() => { setToast('❌ Google Calendar: ' + decodeURIComponent(gcalError)); setTimeout(() => setToast(''), 4000); }, 500);
      }
      if (gcalConnected === '1') {
        setTimeout(() => { setToast('✅ Google Calendar connected!'); setTimeout(() => setToast(''), 4000); }, 500);
      }
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    // Check for pending workspace invite (user was redirected here to login)
    const pendingInvite = sessionStorage.getItem('pending_workspace_invite');
    if (pendingInvite) {
      sessionStorage.removeItem('pending_workspace_invite');
      window.location.href = `/workspace-invite/${pendingInvite}`;
      return;
    }
    // Always go to dashboard on login — unless a PWA install shortcut
    // (manifest.json "shortcuts") asked for a specific section.
    const shortcutView = { journal:'reflections', deepwork:'deepwork', fitness:'fitness' }[new URLSearchParams(window.location.search).get('shortcut')];
    if (shortcutView) {
      setView(shortcutView);
      window.history.replaceState({}, '', window.location.pathname);
    } else {
      setView('dashboard');
    }
    // If arrived via invite link, auto-accept
    if (inviteProjectId && user.email) {
      (async () => {
        try {
          await db.acceptInviteByEmail(user.email, user.id);
          // Clean URL
          window.history.replaceState({}, '', window.location.pathname);
          setInviteProjectId(null);
          setView('projects');
          onToast('✅ You have joined the project!');
          // Reload data
          if (data?.loadAll) data.loadAll();
        } catch(e) { console.warn('[accept invite]', e.message); }
      })();
    }
  }, [user?.id]); // eslint-disable-line
  const [toast,           setToast]          = useState('');
  const [showNewsletter,  setShowNewsletter]  = useState(false);
  const [featureFlags,    setFeatureFlags]    = useState({});

  // Load feature flags from admin config table
  useEffect(() => {
    supabase.from('admin_feature_flags').select('flags').eq('id','global').maybeSingle()
      .then(({ data }) => { if (data?.flags) setFeatureFlags(data.flags); })
      .catch(() => {});
  }, []);
  const [showAuth,        setShowAuth]        = useState(false);  // ← controls login modal
  const [mfaVerified,     setMfaVerified]     = useState(false);
  const [needsMfa,        setNeedsMfa]        = useState(false);

  // MFA check disabled — causes auth lock conflict with useAuth.js getSession
  // Re-enable only when users actually enroll TOTP
  useEffect(() => {
    if (!user) { setMfaVerified(false); setNeedsMfa(false); return; }
    setNeedsMfa(false);
    setMfaVerified(true);
  }, [user]);

  const onToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 4000); };

  // Show newsletter popup after 30s for first-time visitors
  useEffect(() => {
    const seen = localStorage.getItem('waitlist_shown');
    if (!seen) {
      const t = setTimeout(() => { setShowNewsletter(true); localStorage.setItem('waitlist_shown','1'); }, 5000);
      return () => clearTimeout(t);
    }
  }, []);

  if (authLoading || data.loading) return <LoadingScreen/>;

  // MFA challenge — user is logged in but needs second factor
  if (user && needsMfa && !mfaVerified) {
    return <MFALoginChallenge onVerified={() => { setMfaVerified(true); setNeedsMfa(false); }}/>;
  }

  // ── Public pages — accessible without login ──────────────────────────────
  if (bookingUsername) return <PublicBookingPage username={bookingUsername} />;
  if (window.location.pathname === '/superadmin')                    return <SuperAdminPage />;
  if (window.location.pathname.startsWith('/workspace-invite/'))    return <WorkspaceInvitePage />;
  if (window.location.pathname.startsWith('/buy/'))      return <PublicCheckoutPage />;
  if (window.location.pathname.startsWith('/invoice/'))  return <PublicInvoicePage />;
  if (window.location.pathname.startsWith('/proposal/')) return <PublicProposalPage />;
  if (window.location.pathname === '/privacy') return <PrivacyPolicyPage />;

  // ── Not logged in → show Aspreo public website ───────────────────────────
  if (!user) return (
    <>
      <AspreoSite onLogin={() => setShowAuth(true)} onWaitlist={() => setShowNewsletter(true)}/>

      {/* Login modal — slides in when "Get Started" is clicked */}
      {showAuth && (
        <div style={{
          position:'fixed', inset:0, zIndex:9000,
          background:'rgba(0,0,0,.6)', backdropFilter:'blur(6px)',
          display:'flex', alignItems:'center', justifyContent:'center',
          padding:16, animation:'fadeIn .25s ease',
        }} onClick={e => e.target === e.currentTarget && setShowAuth(false)}>
          <div style={{ position:'relative', width:'100%', maxWidth:480 }}>
            {/* Close button */}
            <button
              onClick={() => setShowAuth(false)}
              style={{
                position:'absolute', top:-14, right:-14, zIndex:10,
                width:32, height:32, borderRadius:'50%',
                background:'#1e293b', border:'1px solid rgba(255,255,255,.15)',
                color:'#94a3b8', cursor:'pointer', fontSize:16,
                display:'flex', alignItems:'center', justifyContent:'center',
              }}>✕</button>
            <AuthPage
              onSubscribe={() => { setShowAuth(false); setShowNewsletter(true); }}
              modal
            />
          </div>
        </div>
      )}

      {showNewsletter && <WaitlistPopup onClose={() => setShowNewsletter(false)} profile={profile}/>}
    </>
  );

  // ── Logged in → full app ─────────────────────────────────────────────────
  return (
    <AspreoShell
      view={view}
      setView={setView}
      onBackup={data.exportBackup}
      onShowNewsletter={() => setShowNewsletter(true)}
      featureFlags={featureFlags}
    >
      <Toast message={toast} onClose={() => setToast('')}/>

      {/* ── New device login alert ── */}
      {newLoginAlert && (
        <div style={{ background:'var(--amber-bg)', border:'1px solid var(--amber)', borderLeft:'4px solid var(--amber)', borderRadius:'var(--radius)', padding:'12px 16px', marginBottom:16, display:'flex', alignItems:'center', gap:12, animation:'fadeUp .3s ease' }}>
          <span style={{ fontSize:22 }}>🔐</span>
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:700, color:'var(--amber)', fontSize:13 }}>New device login detected</div>
            <div style={{ fontSize:12, color:'var(--text2)', marginTop:2 }}>
              Signed in from <strong style={{ color:'var(--text)' }}>{newLoginAlert.device}</strong> at {newLoginAlert.time}.
              If this wasn't you, change your password immediately.
            </div>
          </div>
          <button onClick={dismissNewLoginAlert} style={{ background:'none', border:'none', color:'var(--text3)', cursor:'pointer', fontSize:18 }}>✕</button>
        </div>
      )}

      {showNewsletter && <WaitlistPopup onClose={() => setShowNewsletter(false)} profile={profile}/>}

      {view === 'profile'  && <ProfilePage data={data} onToast={onToast}/>}
      {view === 'settings' && <SettingsPageWrapper data={data} onToast={onToast}/>}

      {!['profile','files','settings'].includes(view) && (
        <DiaryApp data={data} view={view} setView={setView} onToast={onToast} profile={profile}/>
      )}
    </AspreoShell>
  );
}

// Settings wrapper
function SettingsPageWrapper({ data, onToast }) {
  const { profile } = useAuth();
  return (
    <div style={{ maxWidth:700, animation:'fadeUp .4s ease' }}>
      <div className="ta-eh-eyebrow">System</div>
      <h1 className="ta-eh-title" style={{ fontSize:32, marginBottom:24 }}>Settings.</h1>
      <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
        <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderTop:`2px solid ${C.sky}`, borderRadius:12, padding:18 }}>
          <h4 style={{ margin:'0 0 8px', color:C.sky, fontSize:13, fontWeight:700 }}>💾 Data & Backup</h4>
          <p style={{ fontSize:13, color:C.text3, margin:'0 0 14px', lineHeight:1.6 }}>
            All your data is saved in real-time to Supabase. You can also export a complete JSON backup at any time.
          </p>
          <button onClick={data.exportBackup} style={{ padding:'9px 18px', borderRadius:8, border:'none', background:C.indigo, color:'#fff', fontFamily:"'Inter',sans-serif", fontSize:13, fontWeight:600, cursor:'pointer' }}>
            ⬇ Export Full Backup
          </button>
        </div>
        <CheckinSettings data={data} onToast={onToast}/>
        <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderTop:`2px solid #8b5cf6`, borderRadius:12, padding:18 }}>
          <h4 style={{ margin:'0 0 8px', color:'#8b5cf6', fontSize:13, fontWeight:700 }}>👤 Account Role</h4>
          <p style={{ fontSize:13, color:C.text3, margin:0 }}>
            Your current role: <strong style={{ color:C.text }}>{profile?.role || 'user'}</strong>
            {profile?.role === 'admin' && ' — You have full admin access.'}
            {profile?.role === 'premium' && ' — You have premium access.'}
          </p>
        </div>
      </div>
    </div>
  );
}

function CheckinSettings({ data, onToast }) {
  const { checkinDefs, addCheckinDef, deleteCheckinDef } = data;
  const [form, setForm] = useState({ id: '', label: '', icon: '' });
  const add = async () => {
    if (!form.label.trim()) return;
    const def = { id: form.id || String(Date.now()), label: form.label, icon: form.icon, sort_order: checkinDefs.length };
    await addCheckinDef(def);
    setForm({ id:'', label:'', icon:'' });
    onToast('✅ Check-in added');
  };
  return (
    <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderTop:'2px solid var(--primary)', borderRadius:'var(--radius)', padding:18, boxShadow:'var(--shadow-sm)' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
        <h4 style={{ margin:0, color:'var(--primary)', fontSize:13, fontWeight:700 }}>☑️ Daily Check-ins</h4>
        <span style={{ fontSize:11, color:'var(--text3)' }}>{checkinDefs.length} item{checkinDefs.length!==1?'s':''}</span>
      </div>
      <p style={{ fontSize:12, color:'var(--text2)', margin:'0 0 14px' }}>These appear as toggles on your dashboard every day.</p>
      <div style={{ display:'flex', flexDirection:'column', gap:6, marginBottom:14 }}>
        {checkinDefs.length===0 && (
          <div style={{ textAlign:'center', padding:'16px', background:'var(--surface-alt)', borderRadius:'var(--radius-sm)', color:'var(--text3)', fontSize:12 }}>
            No check-ins yet. Add your first one below.
          </div>
        )}
        {checkinDefs.map(ci => (
          <div key={ci.id} style={{ display:'flex', alignItems:'center', gap:10, background:'var(--surface-alt)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', padding:'8px 12px' }}>
            <span style={{ fontSize:18, minWidth:26, textAlign:'center' }}>{ci.icon || '☑️'}</span>
            <span style={{ flex:1, fontSize:13, color:'var(--text)', fontWeight:500 }}>{ci.label}</span>
            <button onClick={()=>deleteCheckinDef(ci.id)} style={{ padding:'4px 10px', borderRadius:6, border:'1px solid var(--red-bg,#fef2f2)', background:'var(--red-bg,#fef2f2)', color:'var(--red,#dc2626)', cursor:'pointer', fontSize:11, fontWeight:600 }}>🗑 Remove</button>
          </div>
        ))}
      </div>
      <div style={{ display:'flex', gap:8, paddingTop:12, borderTop:'1px solid var(--border)' }}>
        <input value={form.icon} onChange={e=>setForm(p=>({...p,icon:e.target.value}))} placeholder="🌟" style={{ width:52, padding:'8px 4px', background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', color:'var(--text)', fontFamily:"'Inter',sans-serif", fontSize:18, outline:'none', textAlign:'center' }}/>
        <input value={form.label} onChange={e=>setForm(p=>({...p,label:e.target.value}))} onKeyDown={e=>e.key==='Enter'&&add()} placeholder="e.g. Morning run, Meditate, Read..." style={{ flex:1, padding:'8px 12px', background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', color:'var(--text)', fontFamily:"'Inter',sans-serif", fontSize:13, outline:'none' }}/>
        <button onClick={add} style={{ padding:'8px 18px', borderRadius:'var(--radius-sm)', border:'none', background:'var(--primary)', color:'#fff', fontFamily:"'Inter',sans-serif", fontSize:13, fontWeight:700, cursor:'pointer', flexShrink:0 }}>+ Add</button>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <MainApp/>
      </AuthProvider>
    </ThemeProvider>
  );
}
